"use client";

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent } from "react";
import ProfileBadge from "@/components/common/profile/ProfileBadge";
import { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import type { ApiXTdhGrant } from "@/generated/models/ApiXTdhGrant";
import { cicToType } from "@/helpers/Helpers";
import { isAutoGeneratedHandle, isEthereumAddress } from "@/helpers/AllowlistToolHelpers";
import { shortenAddress } from "@/helpers/address.helpers";
import { formatAmount, formatDateTime } from "@/components/user/xtdh/utils/xtdhGrantFormatters";
import { getGrantStatusLabel } from "../utils";
interface GroupCreateXtdhGrantRowProps {
  readonly grant: ApiXTdhGrant;
  readonly isSelected?: boolean | undefined;
  readonly interactive?: boolean | undefined;
  readonly asListItem?: boolean | undefined;
  readonly className?: string | undefined;
  readonly onSelect?: ((grant: ApiXTdhGrant) => void) | undefined;
}
const getStatusPillClasses = (statusLabel: string): string => {
  if (statusLabel === "ACTIVE") {
    return "tw-bg-green/20 tw-text-green";
  }
  if (statusLabel === "SCHEDULED") {
    return "tw-bg-blue-400/20 tw-text-blue-200";
  }
  if (statusLabel === "ENDED") {
    return "tw-bg-iron-700/30 tw-text-iron-400";
  }
  if (statusLabel === "PENDING") {
    return "tw-bg-primary-400/20 tw-text-primary-300";
  }
  return "tw-bg-red/20 tw-text-red";
};
const getStateClasses = (interactive: boolean, isSelected: boolean): string => {
  if (interactive && isSelected) {
    return "tw-cursor-pointer tw-border-primary-400 tw-bg-primary-500/10";
  }
  if (interactive) {
    return "tw-cursor-pointer tw-border-iron-800 tw-bg-iron-900/70 desktop-hover:hover:tw-border-iron-700 desktop-hover:hover:tw-bg-iron-900";
  }
  if (isSelected) {
    return "tw-border-primary-400 tw-bg-primary-500/10";
  }
  return "tw-border-iron-800 tw-bg-iron-900/60";
};
const getRowClasses = ({
  asListItem,
  interactive,
  stateClasses,
  className
}: {
  readonly asListItem: boolean;
  readonly interactive: boolean;
  readonly stateClasses: string;
  readonly className: string | undefined;
}): string => `${asListItem ? "tw-list-none " : ""}tw-rounded-lg tw-border tw-border-solid tw-p-3 tw-outline-none tw-transition tw-duration-200 ${interactive ? "focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400" : ""} ${stateClasses} ${className ?? ""}`.trim();
const getInteractiveProps = (interactive: boolean, handleSelect: () => void, handleKeyDown: (event: KeyboardEvent<HTMLElement>) => void) => {
  if (!interactive) {
    return {
      role: undefined,
      tabIndex: undefined,
      onClick: undefined,
      onKeyDown: undefined
    };
  }
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: handleSelect,
    onKeyDown: handleKeyDown
  };
};
const isActivationKey = (key: string): boolean => key === "Enter" || key === " ";
export default function GroupCreateXtdhGrantRow(t0) {
  const $ = _c(89);
  const {
    grant,
    isSelected: t1,
    interactive: t2,
    asListItem: t3,
    className,
    onSelect
  } = t0;
  const isSelected = t1 === undefined ? false : t1;
  const interactive = t2 === undefined ? false : t2;
  const asListItem = t3 === undefined ? false : t3;
  let ContainerTag;
  let grantorSummary;
  let interactiveProps;
  let rowClasses;
  let statusLabel;
  let t4;
  let t5;
  let t6;
  let t7;
  let targetLabel;
  if ($[0] !== asListItem || $[1] !== className || $[2] !== grant || $[3] !== interactive || $[4] !== isSelected || $[5] !== onSelect) {
    statusLabel = getGrantStatusLabel({
      status: grant.status,
      validFrom: grant.valid_from,
      validTo: grant.valid_to
    });
    let t8;
    if ($[16] !== grant.target_collection_name) {
      t8 = grant.target_collection_name?.trim() ?? "";
      $[16] = grant.target_collection_name;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    const trimmedCollectionName = t8;
    targetLabel = trimmedCollectionName.length > 0 ? trimmedCollectionName : grant.target_contract || "Unknown target";
    const grantor = grant.grantor;
    const grantorHandle = grantor.handle;
    const grantorPrimaryAddress = grantor.primary_address;
    let t9;
    if ($[18] !== grantorHandle || $[19] !== grantorPrimaryAddress) {
      t9 = grantorHandle ?? shortenAddress(grantorPrimaryAddress);
      $[18] = grantorHandle;
      $[19] = grantorPrimaryAddress;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    const displayGrantor = t9;
    const shouldTruncateGrantor = isEthereumAddress(grantorHandle ?? grantorPrimaryAddress) || isAutoGeneratedHandle(grantorHandle ?? grantorPrimaryAddress);
    const tooltipIdentity = grantorHandle ?? grantor.id;
    const profileHref = grantorHandle ? `/${grantorHandle}` : undefined;
    let t10;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-text-iron-400">?</div>;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const avatarFallback = t10;
    const t11 = grantor.pfp;
    const t12 = grantor.level;
    let t13;
    if ($[22] !== grantor.cic) {
      t13 = cicToType(grantor.cic);
      $[22] = grantor.cic;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    const t14 = `tw-min-w-0 ${shouldTruncateGrantor ? "[&>div>div]:tw-min-w-0 [&>div]:tw-min-w-0 [&_p]:tw-truncate" : ""}`;
    const t15 = grantorHandle ?? "Grantor profile";
    const t16 = Boolean(profileHref);
    let t17;
    if ($[24] !== displayGrantor || $[25] !== grantor.level || $[26] !== grantor.pfp || $[27] !== profileHref || $[28] !== t13 || $[29] !== t14 || $[30] !== t15 || $[31] !== t16) {
      t17 = <ProfileBadge handle={displayGrantor} href={profileHref} pfpUrl={t11} level={t12} cicType={t13} size={ProfileBadgeSize.SMALL} className={t14} avatarAlt={t15} avatarFallback={avatarFallback} asLink={t16} />;
      $[24] = displayGrantor;
      $[25] = grantor.level;
      $[26] = grantor.pfp;
      $[27] = profileHref;
      $[28] = t13;
      $[29] = t14;
      $[30] = t15;
      $[31] = t16;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const grantorBadge = t17;
    let t18;
    if ($[33] !== grantorBadge || $[34] !== tooltipIdentity) {
      t18 = tooltipIdentity ? <UserProfileTooltipWrapper user={tooltipIdentity}>{grantorBadge}</UserProfileTooltipWrapper> : grantorBadge;
      $[33] = grantorBadge;
      $[34] = tooltipIdentity;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    grantorSummary = t18;
    let t19;
    if ($[36] !== grant || $[37] !== onSelect) {
      t19 = () => onSelect?.(grant);
      $[36] = grant;
      $[37] = onSelect;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    const handleSelect = t19;
    let t20;
    if ($[39] !== handleSelect || $[40] !== interactive) {
      t20 = event => {
        if (!interactive || !isActivationKey(event.key)) {
          return;
        }
        event.preventDefault();
        handleSelect();
      };
      $[39] = handleSelect;
      $[40] = interactive;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    const handleKeyDown = t20;
    let t21;
    if ($[42] !== asListItem || $[43] !== className || $[44] !== interactive || $[45] !== isSelected) {
      const stateClasses = getStateClasses(interactive, isSelected);
      t21 = getRowClasses({
        asListItem,
        interactive,
        stateClasses,
        className
      });
      $[42] = asListItem;
      $[43] = className;
      $[44] = interactive;
      $[45] = isSelected;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    rowClasses = t21;
    let t22;
    if ($[47] !== handleKeyDown || $[48] !== handleSelect || $[49] !== interactive) {
      t22 = getInteractiveProps(interactive, handleSelect, handleKeyDown);
      $[47] = handleKeyDown;
      $[48] = handleSelect;
      $[49] = interactive;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    interactiveProps = t22;
    ContainerTag = asListItem ? "li" : "div";
    t7 = "tw-flex tw-flex-col tw-gap-3";
    t6 = "tw-min-w-0 tw-flex-1 tw-space-y-1.5";
    t5 = "tw-flex tw-flex-wrap tw-items-center tw-gap-2";
    t4 = getStatusPillClasses(statusLabel);
    $[0] = asListItem;
    $[1] = className;
    $[2] = grant;
    $[3] = interactive;
    $[4] = isSelected;
    $[5] = onSelect;
    $[6] = ContainerTag;
    $[7] = grantorSummary;
    $[8] = interactiveProps;
    $[9] = rowClasses;
    $[10] = statusLabel;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = targetLabel;
  } else {
    ContainerTag = $[6];
    grantorSummary = $[7];
    interactiveProps = $[8];
    rowClasses = $[9];
    statusLabel = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    targetLabel = $[15];
  }
  const t8 = `tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-tracking-wide ${t4}`;
  let t9;
  if ($[51] !== statusLabel || $[52] !== t8) {
    t9 = <span className={t8}>{statusLabel}</span>;
    $[51] = statusLabel;
    $[52] = t8;
    $[53] = t9;
  } else {
    t9 = $[53];
  }
  let t10;
  if ($[54] !== targetLabel) {
    t10 = <p className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-iron-50">{targetLabel}</p>;
    $[54] = targetLabel;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  let t11;
  if ($[56] !== t10 || $[57] !== t5 || $[58] !== t9) {
    t11 = <div className={t5}>{t9}{t10}</div>;
    $[56] = t10;
    $[57] = t5;
    $[58] = t9;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== grantorSummary) {
    t12 = <div className="tw-min-w-0 tw-flex-1">{grantorSummary}</div>;
    $[60] = grantorSummary;
    $[61] = t12;
  } else {
    t12 = $[61];
  }
  let t13;
  if ($[62] !== grant.rate) {
    t13 = formatAmount(grant.rate);
    $[62] = grant.rate;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== t13) {
    t14 = <p className="tw-m-0 tw-whitespace-nowrap tw-text-xs tw-font-semibold tw-text-iron-400">Rate: {t13}</p>;
    $[64] = t13;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== t12 || $[67] !== t14) {
    t15 = <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">{t12}{t14}</div>;
    $[66] = t12;
    $[67] = t14;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  const t16 = grant.valid_from ?? null;
  let t17;
  if ($[69] !== t16) {
    t17 = formatDateTime(t16, {
      fallbackLabel: "Immediately",
      includeTime: false
    });
    $[69] = t16;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  const t18 = grant.valid_to ?? null;
  let t19;
  if ($[71] !== t18) {
    t19 = formatDateTime(t18, {
      fallbackLabel: "No expiry",
      includeTime: false
    });
    $[71] = t18;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  let t20;
  if ($[73] !== t17 || $[74] !== t19) {
    t20 = <p className="tw-m-0 tw-text-xs tw-text-iron-500">Valid:{" "}{t17}{" "}{"->"}{" "}{t19}</p>;
    $[73] = t17;
    $[74] = t19;
    $[75] = t20;
  } else {
    t20 = $[75];
  }
  let t21;
  if ($[76] !== t11 || $[77] !== t15 || $[78] !== t20 || $[79] !== t6) {
    t21 = <div className={t6}>{t11}{t15}{t20}</div>;
    $[76] = t11;
    $[77] = t15;
    $[78] = t20;
    $[79] = t6;
    $[80] = t21;
  } else {
    t21 = $[80];
  }
  let t22;
  if ($[81] !== t21 || $[82] !== t7) {
    t22 = <div className={t7}>{t21}</div>;
    $[81] = t21;
    $[82] = t7;
    $[83] = t22;
  } else {
    t22 = $[83];
  }
  const rowContent = t22;
  let t23;
  if ($[84] !== ContainerTag || $[85] !== interactiveProps || $[86] !== rowClasses || $[87] !== rowContent) {
    t23 = <ContainerTag className={rowClasses} {...interactiveProps}>{rowContent}</ContainerTag>;
    $[84] = ContainerTag;
    $[85] = interactiveProps;
    $[86] = rowClasses;
    $[87] = rowContent;
    $[88] = t23;
  } else {
    t23 = $[88];
  }
  return t23;
}